
import { v4 as uuidv4 } from "uuid";
import { 
  PlayerObject, 
  SmartHash,
  UpsertableBase
} from "@models/UpsertableBase";
import { 
  AdventureSpread,
  Character, 
  CharacterOptions,
  CharacterSubscriptionHolder,
  ComplexCreatureInstance,
  Encounter,
  ItemLimitation,
  Recipe, 
} from "@dndModels";
import { DataType } from "@dndModels/Types";
import { User } from "@models";

import CommonUtilities from "@utilities/common_utilities";

const common_util = CommonUtilities.getInstance();

export class Campaign extends PlayerObject {
  data_type: DataType = "campaign";
  subscription_holder = new CharacterSubscriptionHolder();
  archived_data_holder: SmartHash = {};
  last_changed = 0;
  last_saved = 0;

  changed() {
    this.last_changed = common_util.get_time_number();
    this.subscription_holder.campaignChanged();
  }

  old_player_ids: string[] = [];
  c_players?: CampaignPlayers;
  get player_ids() {
    if (this.c_players) {
      return this.c_players.player_ids;
    }
    return this.old_player_ids;
  }
  get players() {
    if (this.c_players) {
      return this.c_players.the_players;
    }
    return [];
  }

  old_reusable_invite = "";
  get reusable_invite() {
    if (this.c_players) {
      return this.c_players.reusable_invite;
    }
    return this.old_reusable_invite;
  }
  get invites() {
    if (this.c_players) {
      return this.c_players.invites;
    }
    return [];
  }
  set invites(value: string[]) {
    if (this.c_players) {
      this.c_players.invites = value;
    }
  }

  old_character_options = new CharacterOptions();
  char_options?: CampaignCharOptions;
  get character_options() {
    if (this.char_options) {
      return this.char_options.char_options;
    }
    return this.old_character_options;
  }

  c_chars: CampaignChar[] = [];
  get characters() {
    return this.c_chars.map(o => o.char).filter(o => o !== undefined) as Character[];
  }
  // org_inst_ids: string[] = [];
  // veh_inst_ids: string[] = [];
  // decks_of_many_things: { id: string, visible: boolean }[] = [];
  c_decks: CampaignDeck[] = [];
  is_favorite = false;
  description = "";
  last_updated = 0;

  campaign_recipes_loaded = false;
  campaign_recipes: CampaignRecipe[] = [];

  notes: CampaignNote[] = [];
  npcs: CampaignNPC[] = [];
  encounters: Encounter[] = [];

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.old_player_ids = obj.player_ids ? obj.player_ids : [];
      this.old_reusable_invite = obj.reusable_invite ? obj.reusable_invite : "";
      if (obj.character_options) {
        this.old_character_options = new CharacterOptions(obj.character_options);
      } 
      this.is_favorite = obj.is_favorite ? obj.is_favorite : false;
      // this.org_inst_ids = obj.org_inst_ids ? obj.org_inst_ids : [];
      // this.veh_inst_ids = obj.veh_inst_ids ? obj.veh_inst_ids : [];
      // this.decks_of_many_things = obj.decks_of_many_things ? obj.decks_of_many_things : [];
      this.description = obj.description ? obj.description : "";
      this.last_updated = obj.last_updated ? obj.last_updated : 0;
    }
    this.old_player_ids = this.player_ids.filter(o => o !== "");
  }

  get true_name() {
    return this.name;
  }

  toDBObj(): any {
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      character_options: this.character_options.toDBObj(),
      is_favorite: this.is_favorite,
      // org_inst_ids: this.org_inst_ids,
      // veh_inst_ids: this.veh_inst_ids,
      // decks_of_many_things: this.decks_of_many_things,
      description: this.description,
      last_updated: this.last_updated,
    };
  }

  copy(copyMe: Campaign, drop_ids = false): void {
    super.copy(copyMe, drop_ids);

    this.old_player_ids = copyMe.old_player_ids;
    this.c_players = copyMe.c_players ? copyMe.c_players.clone() : undefined;
    
    this.old_character_options = copyMe.old_character_options.clone();
    this.char_options = copyMe.char_options;
    
    // this.org_inst_ids = [...copyMe.org_inst_ids];
    // this.veh_inst_ids = [...copyMe.veh_inst_ids];
    // this.decks_of_many_things = copyMe.decks_of_many_things.map(o => { return {...o}; });
    this.c_decks = [...copyMe.c_decks];
    this.c_chars = [...copyMe.c_chars];
    this.description = copyMe.description;
    this.last_updated = copyMe.last_updated;
    this.notes = [...copyMe.notes];
    this.npcs = [...copyMe.npcs];
    this.encounters = [...copyMe.encounters];
  }

  clone(drop_ids = false): Campaign {
    const new_obj = new Campaign();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
export class CampaignObject extends UpsertableBase {
  data_type: DataType = "campaign_object";
  campaign_id = "";
  campaign?: Campaign;

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.campaign_id = obj.campaign_id ? obj.campaign_id : "";
    }
  }

  toDBObj(): any {
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      campaign_id: this.campaign_id,
    };
  }

  copy(copyMe: CampaignObject, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.campaign_id = copyMe.campaign_id;
  }

  clone(drop_ids = false): CampaignObject {
    const new_obj = new CampaignObject();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
export class CampaignPlayers extends CampaignObject {
  data_type: DataType = "campaign_players";
  players: CampaignPlayer[] = [];
  invites: string[] = [];
  reusable_invite = "";

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.invites = obj.invites ? obj.invites : [];
      this.reusable_invite = obj.reusable_invite ? obj.reusable_invite : uuidv4().toString();
    }
  }

  get the_players() {
    return this.players.filter(o => o.player !== undefined).map(o => o.player) as User[];
  }

  get player_ids() {
    return this.players.map(o => o.player_id);
  }

  toDBObj(): any {
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      invites: this.invites,
      reusable_invite: this.reusable_invite,
    };
  }

  generate_invite(): string {
    const invite = uuidv4().toString();
    this.invites.push(invite);
    return invite;
  }

  copy(copyMe: CampaignPlayers, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.players = [...copyMe.players];
    this.invites = [...copyMe.invites];
    this.reusable_invite = copyMe.reusable_invite;
  }

  clone(drop_ids = false): CampaignPlayers {
    const new_obj = new CampaignPlayers();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
export class CampaignPlayer extends CampaignObject {
  data_type: DataType = "campaign_player";
  player_id = "";
  player?: User;
  campaign_is_favorite = false;

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.player_id = obj.player_id ? obj.player_id : "";
      this.campaign_is_favorite = obj.campaign_is_favorite ? obj.campaign_is_favorite : false;
    }
  }

  get true_name() {
    return this.name;
  }

  toDBObj(): any {
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      player_id: this.player_id,
      campaign_is_favorite: this.campaign_is_favorite,
    };
  }

  copy(copyMe: CampaignPlayer, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.player_id = copyMe.player_id;
    this.campaign_is_favorite = copyMe.campaign_is_favorite;
  }

  clone(drop_ids = false): CampaignPlayer {
    const new_obj = new CampaignPlayer();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
export class CampaignRecipe extends CampaignObject {
  data_type: DataType = "campaign_recipe";
  for_type: "Item" | "Magic Item Keyword" = "Item";
  for_id = "";
  recipes: Recipe[] = [];

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.for_type = obj.for_type ? obj.for_type : "Item";
      this.for_id = obj.for_id ? obj.for_id : "";
      this.recipes = obj.recipes ? obj.recipes.map((o: any) => new Recipe(o)) : [];
    }
  }

  toDBObj(): any {
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      for_type: this.for_type,
      for_id: this.for_id,
      recipes: this.recipes.map(o => o.toDBObj())
    };
  }

  copy(copyMe: CampaignRecipe, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.for_type = copyMe.for_type;
    this.for_id = copyMe.for_id;
    this.recipes = copyMe.recipes.map(o => o.clone(drop_ids));
  }

  clone(drop_ids = false): CampaignRecipe {
    const new_obj = new CampaignRecipe();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
export class CampaignChar extends CampaignObject {
  data_type: DataType = "campaign_char";
  char_id = "";
  active = true;
  char?: Character;

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.char_id = obj.char_id ? obj.char_id : "";
      this.active = obj.active !== undefined ? obj.active : true;
    }
  }

  get true_name() {
    return this.name;
  }

  toDBObj(): any {
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      char_id: this.char_id,
      active: this.active,
    };
  }

  copy(copyMe: CampaignChar, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.char_id = copyMe.char_id;
    this.active = copyMe.active;
  }

  clone(drop_ids = false): CampaignChar {
    const new_obj = new CampaignChar();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
export class CampaignCharOptions extends CampaignObject {
  data_type: DataType = "campaign_char_options";
  char_options: CharacterOptions = new CharacterOptions();

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.char_options = obj.char_options ? new CharacterOptions(obj.char_options) : new CharacterOptions();;
    }
  }

  get true_name() {
    return this.name;
  }

  toDBObj(): any {
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      char_options: this.char_options.toDBObj(),
    };
  }

  copy(copyMe: CampaignCharOptions, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.char_options = copyMe.char_options.clone();
  }

  clone(drop_ids = false): CampaignCharOptions {
    const new_obj = new CampaignCharOptions();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
export class CampaignDeck extends CampaignObject {
  data_type: DataType = "campaign_deck";
  deck_id = "";
  card_ids: string[] = [];
  drawn_card_ids: string[] = [];
  saved_spreads: AdventureSpread[] = [];
  fudged_draw_ids: string[] = []; // This allows the DM select a particular card or set of cards to be drawn rather than the standard list.  Yes, it's cheating, but if done to make things more fun for the players then I'm okay with it.
  fudged_for_beneficial = false;

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.deck_id = obj.deck_id ? obj.deck_id : "";
      this.card_ids = obj.card_ids ? obj.card_ids : [];
      this.drawn_card_ids = obj.drawn_card_ids ? obj.drawn_card_ids : [];
      this.saved_spreads = obj.saved_spreads ? obj.saved_spreads : [];
      this.fudged_draw_ids = obj.fudged_draw_ids ? obj.fudged_draw_ids : [];
      this.fudged_for_beneficial = obj.fudged_for_beneficial ? obj.fudged_for_beneficial : false;
    }
  }

  toDBObj(): any {
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      deck_id: this.deck_id,
      card_ids: this.card_ids,
      drawn_card_ids: this.drawn_card_ids,
      saved_spreads: this.saved_spreads,
      fudged_draw_ids: this.fudged_draw_ids,
      fudged_for_beneficial: this.fudged_for_beneficial,
    };
  }

  copy(copyMe: CampaignDeck, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.deck_id = copyMe.deck_id;
    this.card_ids = [...copyMe.card_ids];
    this.drawn_card_ids = [...copyMe.drawn_card_ids];
    this.saved_spreads = copyMe.saved_spreads.map(o => { 
      return { id: o.id, spread: o.spread };
    });
    this.fudged_draw_ids = [...copyMe.fudged_draw_ids];
    this.fudged_for_beneficial = copyMe.fudged_for_beneficial;
  }

  clone(drop_ids = false): CampaignDeck {
    const new_obj = new CampaignDeck();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class CampaignNote extends CampaignObject {
  data_type: DataType = "campaign_note";
  notes = "";
  position: { x: number, y: number } = { x: 0, y: 0 };

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.notes = obj.notes ? obj.notes : "";
      this.position = obj.position ? obj.position : { x: 0, y: 0 };
    }
  }

  toDBObj(): any {
    return {
      ...super.toDBObj(),
      notes: this.notes,
      position: this.position
    };
  }

  copy(copyMe: CampaignNote, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.notes = copyMe.notes;
    this.position = {...copyMe.position};
  }

  clone(drop_ids = false): CampaignNote {
    const new_obj = new CampaignNote();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
export class CampaignNPC extends CampaignObject {
  data_type: DataType = "campaign_npc";
  notes = "";
  position: { x: number, y: number } = { x: 0, y: 0 };
  current_location = "";
  ci = new ComplexCreatureInstance();
  char_id = "";
  char_sheet?: Character;
  inventory: VendorInvItem[] = []; 
  // This holds all the items a vendor has to sell. 
  // They are a set of Item Limits with associated counts (can be numbers or strings to account for whatever they might need to be) 
  // and prices (also can be numbers or strings).
  services: VendorService[] = []; 
  // Similar to inventory, but instead its services like Training, Healing, Cleansing, Resurrection, Transportation, Sending, etc.
  // Because of the nature of this, these are really more like additional notes, but with prices attached.

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.notes = obj.notes ? obj.notes : "";
      this.position = obj.position ? obj.position : { x: 0, y: 0 };
      this.current_location = obj.current_location ? obj.current_location : "";
      this.ci = obj.ci ? new ComplexCreatureInstance(obj.ci) : new ComplexCreatureInstance();
      this.char_id = obj.char_id ? obj.char_id : "";
      this.inventory = obj.inventory ? obj.inventory.map((o: any) => new VendorInvItem(o)) : [];
      this.services = obj.services ? obj.services.map((o: any) => new VendorService(o)) : [];
    }
  }

  toDBObj(): any {
    return {
      ...super.toDBObj(),
      notes: this.notes,
      position: this.position,
      current_location: this.current_location,
      ci: this.ci.toDBObj(),
      char_id: this.char_id,
      inventory: this.inventory.map(o => o.toDBObj()),
      services: this.services.map(o => o.toDBObj())
    };
  }

  copy(copyMe: CampaignNPC, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.name = copyMe.name;
    this.notes = copyMe.notes;
    this.position = {...copyMe.position};
    this.current_location = copyMe.current_location;
    this.ci = copyMe.ci.clone();
    this.char_id = copyMe.char_id;
    this.char_sheet = copyMe.char_sheet;
    this.inventory = copyMe.inventory.map(o => o.clone());
    this.services = copyMe.services.map(o => o.clone());
  }

  clone(drop_ids = false): CampaignNPC {
    const new_obj = new CampaignNPC();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
export class VendorInvItem {
  true_id = "";
  name = "";
  item_limit = new ItemLimitation();
  amount = "";
  price = "";

  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.name = obj.name ? obj.name : "";
      this.item_limit = obj.item_limit ? new ItemLimitation(obj.item_limit) : new ItemLimitation();
      this.amount = obj.amount ? obj.amount : "";
      this.price = obj.price ? obj.price : "";
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      name: this.name,
      item_limit: this.item_limit.toDBObj(),
      amount: this.amount,
      price: this.price,
    };
  }

  copy(copyMe: VendorInvItem, drop_ids = false): void {
    this.true_id = copyMe.true_id;
    this.name = copyMe.name;
    this.item_limit = copyMe.item_limit.clone();
    this.amount = copyMe.amount;
    this.price = copyMe.price;
  }

  clone(drop_ids = false): VendorInvItem {
    const new_obj = new VendorInvItem();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
export class VendorService {
  true_id = "";
  name = "";
  notes = "";
  amount = "";
  price = "";

  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.name = obj.name ? obj.name : "";
      this.notes = obj.notes ? obj.notes : "";
      this.amount = obj.amount ? obj.amount : "";
      this.price = obj.price ? obj.price : "";
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      name: this.name,
      notes: this.notes,
      amount: this.amount,
      price: this.price,
    };
  }

  copy(copyMe: VendorService, drop_ids = false): void {
    this.true_id = copyMe.true_id;
    this.name = copyMe.name;
    this.notes = copyMe.notes;
    this.amount = copyMe.amount;
    this.price = copyMe.price;
  }

  clone(drop_ids = false): VendorService {
    const new_obj = new VendorService();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}